import { useState, useMemo } from "react";
import { Search, Upload, FileText, File, AlertTriangle, FolderOpen, Share2 } from "lucide-react";
import { Select, Modal, Form, Input, DatePicker, Button } from "antd";
const documentsData: any[] = [];

const DOC_TYPE_CFG: Record<string, string> = {
  "Insurance":         "bg-blue-50 text-blue-700 border border-blue-200",
  "Risk Assessment":   "bg-orange-50 text-orange-700 border border-orange-200",
  "Inspection Report": "bg-indigo-50 text-indigo-700 border border-indigo-200",
  "Worker Certificate":"bg-purple-50 text-purple-700 border border-purple-200",
  "Method Statement":  "bg-teal-50 text-teal-700 border border-teal-200",
  "COSHH Assessment":  "bg-red-50 text-red-600 border border-red-200",
  "Service Sheet":     "bg-emerald-50 text-emerald-700 border border-emerald-200",
};

const FILE_ICON: Record<string, React.ReactNode> = {
  pdf:  <FileText size={14} className="text-red-500" />,
  docx: <File size={14} className="text-blue-500" />,
  xlsx: <File size={14} className="text-emerald-500" />,
};

const isExpiringSoon = (dateStr?: string) => {
  if (!dateStr) return null;
  const parts = dateStr.split(" ");
  const months: Record<string, number> = { Jan:0,Feb:1,Mar:2,Apr:3,May:4,Jun:5,Jul:6,Aug:7,Sep:8,Oct:9,Nov:10,Dec:11 };
  const d = new Date(parseInt(parts[2]), months[parts[1]], parseInt(parts[0]));
  const diff = (d.getTime() - Date.now()) / (1000 * 60 * 60 * 24);
  return diff < 0 ? "overdue" : diff < 60 ? "soon" : "ok";
};

const SummaryCard = ({ icon, iconBg, value, label, sub }: { icon: React.ReactNode; iconBg: string; value: string | number; label: string; sub?: string }) => (
  <div className="bg-white rounded-xl p-4 border border-slate-100 shadow-sm flex items-center gap-4">
    <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${iconBg}`}>{icon}</div>
    <div>
      <div className="text-[11px] text-slate-400 uppercase tracking-wide font-medium">{label}</div>
      <div className="text-2xl font-bold text-slate-800 leading-tight">{value}</div>
      {sub && <div className="text-[11px] text-slate-400 mt-0.5">{sub}</div>}
    </div>
  </div>
);

const DocumentsPage = () => {
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("All");
  const [modalOpen, setModalOpen] = useState(false);
  const [form] = Form.useForm();

  const docTypes = ["All", ...Array.from(new Set(documentsData.map((d) => d.docType)))];

  const filtered = useMemo(() => documentsData.filter((d) => {
    const matchSearch = !search || d.name.toLowerCase().includes(search.toLowerCase()) || (d.client || "").toLowerCase().includes(search.toLowerCase());
    const matchType = typeFilter === "All" || d.docType === typeFilter;
    return matchSearch && matchType;
  }), [search, typeFilter]);

  const expiring  = documentsData.filter((d) => isExpiringSoon(d.expiryDate) === "soon" || isExpiringSoon(d.expiryDate) === "overdue").length;
  const shared    = documentsData.filter((d) => d.isSharedWithClient).length;
  const totalSize = Math.round(documentsData.reduce((s, d) => s + d.fileSizeKb, 0) / 1024 * 10) / 10;

  return (
    <div className="p-6 min-h-screen bg-slate-50/50">
      {/* HEADER */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-semibold text-slate-800">Documents</h1>
          <p className="text-slate-400 text-sm">Certificates, reports, risk assessments and compliance files</p>
        </div>
        <Button type="primary" icon={<Upload size={15} />} onClick={() => setModalOpen(true)}
          className="flex items-center gap-1.5 h-9 rounded-lg">Upload Document</Button>
      </div>

      {/* SUMMARY */}
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
        <SummaryCard icon={<FolderOpen size={18} className="text-blue-500" />} iconBg="bg-blue-50" value={documentsData.length} label="Total Documents" sub={`${totalSize} MB stored`} />
        <SummaryCard icon={<AlertTriangle size={18} className="text-amber-500" />} iconBg="bg-amber-50" value={expiring} label="Expiry Alerts" sub="expiring within 60 days" />
        <SummaryCard icon={<Share2 size={18} className="text-indigo-500" />} iconBg="bg-indigo-50" value={shared} label="Shared with Clients" sub="client portal visible" />
        <SummaryCard icon={<FileText size={18} className="text-emerald-500" />} iconBg="bg-emerald-50" value={Array.from(new Set(documentsData.map((d) => d.docType))).length} label="Document Types" sub="categories" />
      </div>

      {/* FILTERS */}
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-48">
          <Search size={14} className="text-slate-400 absolute left-3 top-2.5 z-10" />
          <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search documents or client…"
            className="pl-8 pr-4 py-2 text-sm border border-slate-200 rounded-lg w-full bg-white focus:outline-none focus:border-primary" />
        </div>
        <Select value={typeFilter} onChange={setTypeFilter} className="w-44"
          options={docTypes.map((t) => ({ value: t, label: t === "All" ? "All Types" : t }))} />
      </div>

      {/* TABLE */}
      <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-[11px] text-slate-400 font-semibold uppercase tracking-wide border-b border-slate-100">
                <th className="text-left px-5 py-3">Document</th>
                <th className="text-left py-3">Type</th>
                <th className="text-left py-3">Client</th>
                <th className="text-left py-3">Job Ref</th>
                <th className="text-left py-3">Uploaded By</th>
                <th className="text-left py-3">Uploaded</th>
                <th className="text-left py-3">Expiry</th>
                <th className="text-right py-3">Size</th>
                <th className="text-center py-3">Shared</th>
                <th className="text-center py-3">Ver.</th>
                <th className="py-3 pr-5"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((d) => {
                const expiryState = isExpiringSoon(d.expiryDate);
                return (
                  <tr key={d.id} className="border-t border-slate-50 hover:bg-slate-50/70 cursor-pointer transition-colors">
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2">
                        {FILE_ICON[d.fileType] || <File size={14} className="text-slate-400" />}
                        <div>
                          <div className="font-medium text-slate-800 text-sm">{d.name}</div>
                          <div className="text-[10px] text-slate-400 uppercase">{d.fileType}</div>
                        </div>
                      </div>
                    </td>
                    <td className="py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ${DOC_TYPE_CFG[d.docType] || "bg-slate-50 text-slate-500 border border-slate-200"}`}>
                        {d.docType}
                      </span>
                    </td>
                    <td className="py-3 text-slate-500 text-xs">{d.client || "—"}</td>
                    <td className="py-3 text-xs font-mono text-primary">{d.jobRef || "—"}</td>
                    <td className="py-3 text-slate-500 text-xs">{d.uploadedBy}</td>
                    <td className="py-3 text-slate-500 text-xs whitespace-nowrap">{d.uploadedAt}</td>
                    <td className="py-3">
                      {d.expiryDate ? (
                        <span className={`text-xs font-semibold ${expiryState === "overdue" ? "text-red-600" : expiryState === "soon" ? "text-amber-600" : "text-slate-500"}`}>
                          {d.expiryDate}
                        </span>
                      ) : <span className="text-slate-300 text-xs">—</span>}
                    </td>
                    <td className="py-3 text-right text-slate-400 text-xs">{d.fileSizeKb} KB</td>
                    <td className="py-3 text-center">
                      {d.isSharedWithClient
                        ? <span className="text-emerald-500 text-xs font-bold">✓</span>
                        : <span className="text-slate-300 text-xs">—</span>}
                    </td>
                    <td className="py-3 text-center text-slate-400 text-xs">v{d.version}</td>
                    <td className="py-3 pr-5">
                      <button className="text-xs text-blue-600 hover:underline">Download</button>
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr><td colSpan={11} className="px-5 py-10 text-center text-slate-400 text-sm">No documents match.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="px-5 py-2.5 border-t border-slate-100 text-xs text-slate-400">
          Showing {filtered.length} of {documentsData.length} documents
        </div>
      </div>

      {/* UPLOAD MODAL */}
      <Modal open={modalOpen} onCancel={() => { setModalOpen(false); form.resetFields(); }}
        title={<span className="text-base font-semibold">Upload Document</span>} width={480}
        footer={[
          <Button key="cancel" onClick={() => { setModalOpen(false); form.resetFields(); }}>Cancel</Button>,
          <Button key="submit" type="primary" onClick={() => form.submit()}>Upload</Button>,
        ]}>
        <Form form={form} layout="vertical" size="middle" className="pt-2" onFinish={() => setModalOpen(false)}>
          <Form.Item name="name" label="Document Name" rules={[{ required: true }]}>
            <Input placeholder="e.g. HVAC Service Certificate — Premier Inn" />
          </Form.Item>
          <div className="grid grid-cols-2 gap-x-4">
            <Form.Item name="docType" label="Document Type" rules={[{ required: true }]}>
              <Select placeholder="Select type" options={["Insurance","Risk Assessment","Inspection Report","Worker Certificate","Method Statement","COSHH Assessment","Service Sheet","Other"].map(t => ({ value: t, label: t }))} />
            </Form.Item>
            <Form.Item name="expiryDate" label="Expiry Date (if applicable)">
              <DatePicker className="w-full" />
            </Form.Item>
          </div>
          <div className="grid grid-cols-2 gap-x-4">
            <Form.Item name="client" label="Link to Client">
              <Select placeholder="Optional" allowClear options={["Premier Inn Ltd","Tesco PLC","NHS Trust Herts","Bright Homes Ltd"].map(c => ({ value: c, label: c }))} />
            </Form.Item>
            <Form.Item name="jobRef" label="Link to Job (optional)">
              <Input placeholder="JOB-XXXX" />
            </Form.Item>
          </div>
          <Form.Item name="shareWithClient" label="Share with Client Portal">
            <Select defaultValue="no" options={[{ value: "yes", label: "Yes — visible to client" }, { value: "no", label: "No — internal only" }]} />
          </Form.Item>
          <div className="border-2 border-dashed border-slate-200 rounded-xl p-8 text-center">
            <Upload size={24} className="mx-auto mb-2 text-slate-300" />
            <p className="text-sm text-slate-400">Drag & drop or click to upload</p>
            <p className="text-xs text-slate-300 mt-1">PDF, DOCX, XLSX up to 20MB</p>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default DocumentsPage;
